// components/Admin/ActualiteEditor.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Save, Trash2, ArrowLeft, Loader } from 'lucide-react'
import { supabase } from '@lib/supabaseClient'
import RichTextEditor from './RichTextEditor'
import ImageUploader from './ImageUploader'

const ActualiteEditor = ({ actualiteId = null, type = 'Vente', onBack }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState([])
  const [currentUser, setCurrentUser] = useState(null)

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    detailed_description: '',
    category_id: '',
    type: type,
    image_url: '',
    additional_images: [],
    website_url: '',
    is_published: true
  })

  // Charger les catégories
  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('categories')
        .select('*')
        .eq('type', formData.type)
        .order('name')

      if (data) {
        setCategories(data)
        if (data.length > 0 && !formData.category_id) {
          setFormData(prev => ({ ...prev, category_id: data[0].id }))
        }
      }
    }

    fetchCategories()
  }, [formData.type])

  // Charger l'actualité existante
  useEffect(() => {
    const fetchActualite = async () => {
      if (!actualiteId) {
        const { data: { user } } = await supabase.auth.getUser()
        setCurrentUser(user)
        return
      }

      setLoading(true)
      try {
        const { data, error } = await supabase
          .from('actualites')
          .select('*')
          .eq('id', actualiteId)
          .single()

        if (error) throw error

        setFormData(data)
        setCurrentUser({ id: data.created_by })
      } catch (error) {
        console.error('Erreur:', error)
        alert('Erreur lors du chargement de l\'actualité')
      } finally {
        setLoading(false)
      }
    }

    fetchActualite()
  }, [actualiteId])

  const handleChange = (e) => {
    const { name, value, type: inputType } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? e.target.checked : value
    }))
  }

  const handleImageSelect = (url) => {
    setFormData(prev => ({ ...prev, image_url: url }))
  }

  const handleSave = async () => {
    if (!formData.titre || !formData.description || !formData.detailed_description || !formData.image_url) {
      alert('Veuillez remplir tous les champs obligatoires')
      return
    }

    setSaving(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()

      const dataToSave = {
        ...formData,
        created_by: actualiteId ? formData.created_by : user.id,
        updated_at: new Date().toISOString()
      }

      if (actualiteId) {
        const { error } = await supabase
          .from('actualites')
          .update(dataToSave)
          .eq('id', actualiteId)

        if (error) throw error
        alert('Actualité mise à jour avec succès !')
      } else {
        const { error } = await supabase
          .from('actualites')
          .insert([dataToSave])

        if (error) throw error
        alert('Actualité créée avec succès !')
        setFormData({
          titre: '',
          description: '',
          detailed_description: '',
          category_id: categories[0]?.id || '',
          type: formData.type,
          image_url: '',
          additional_images: [],
          website_url: '',
          is_published: true
        })
      }

      onBack()
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!actualiteId || !confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) {
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('actualites')
        .delete()
        .eq('id', actualiteId)

      if (error) throw error
      alert('Actualité supprimée avec succès !')
      onBack()
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la suppression')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-green-500" />
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full pb-20 space-y-8"
    >
      {/* Espacement en haut */}
      <div className="h-20 md:h-24"></div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 px-4 md:px-8">
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </motion.button>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            {actualiteId ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
          </h2>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          {actualiteId && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDelete}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-700/50 text-white rounded-lg transition-colors flex-1 sm:flex-none"
            >
              <Trash2 className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">Supprimer</span>
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-700/50 text-white rounded-lg transition-colors flex-1 sm:flex-none"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin flex-shrink-0" /> : <Save className="w-4 h-4 flex-shrink-0" />}
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </motion.button>
        </div>
      </div>

      {/* Grid: Formulaire + Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 md:px-8">
        {/* Formulaire */}
        <div className="space-y-8">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Titre *</label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              placeholder="Titre de l'actualité"
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
            />
          </div>

          {/* Description courte */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Description courte *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Résumé affiché sur la carte"
              rows="3"
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 resize-none transition-colors"
            />
          </div>

          {/* Image principale */}
          <ImageUploader 
            onImageSelect={handleImageSelect}
            label="Image principale *"
          />

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Catégorie ({formData.type}) *</label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* URL website */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Lien externe (optionnel)</label>
            <input
              type="url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
            />
          </div>

          {/* Publié */}
          <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <input
              type="checkbox"
              name="is_published"
              id="published"
              checked={formData.is_published}
              onChange={handleChange}
              className="w-4 h-4 rounded cursor-pointer"
            />
            <label htmlFor="published" className="text-sm text-gray-300 cursor-pointer">Publier cette actualité</label>
          </div>
        </div>

        {/* Preview + Contenu détaillé */}
        <div className="space-y-8">
          {/* Contenu détaillé */}
          <RichTextEditor
            value={formData.detailed_description}
            onChange={(value) => setFormData(prev => ({ ...prev, detailed_description: value }))}
            placeholder="Contenu détaillé de l'actualité..."
          />

          {/* Preview carte */}
          {formData.image_url && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/60 rounded-lg overflow-hidden backdrop-blur-sm p-5 border border-slate-700 sticky top-24"
            >
              <p className="text-sm text-gray-400 mb-4 font-medium">📋 Aperçu de la carte</p>
              <img
                src={formData.image_url}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <p className="font-semibold text-white mb-2 line-clamp-2">{formData.titre || 'Titre...'}</p>
              <p className="text-sm text-gray-300 line-clamp-3">{formData.description || 'Description...'}</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Espacement en bas */}
      <div className="h-10"></div>
    </motion.div>
  )
}

export default ActualiteEditor